import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "../styles/SelectBonusRaces.css";

import vallon from "../../medias/png/icons/vallon.png";
import chrono from "../../medias/png/icons/chrono.png";
import plat from "../../medias/png/icons/plat.png";
import montagne from "../../medias/png/icons/montagne.png";

const SelectBonusRace = ({
  selectedRaces,
  setBonusRaces,
  bonusRaces,    
  races,
  prevStep,
  saveData,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const icons = {
    Vallon: vallon,
    Chrono: chrono,
    Plat: plat,
    Montagne: montagne,
  };

  const [confirmValidDataPopup, setConfirmValidDataPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [divisionFilter, setDivisionFilter] = useState("all");

  const parseDate = (dateStr) => {
    if (!dateStr) return new Date(0);
    if (typeof dateStr === "string" && dateStr.includes("-") && dateStr.split("-")[0].length === 4) {
      return new Date(dateStr);
    }
    if (typeof dateStr === "string") {
      const parts = dateStr.split("-");
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day);
      }
    }
    return new Date(dateStr);
  };

  const formatDateForDisplay = (dateStr) => {
    const d = parseDate(dateStr);
    if (d.getFullYear() === 1970) return dateStr;

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const isRacePast = (dateStr) => {
    const raceDate = parseDate(dateStr);
    
    const limitDate = new Date();
    limitDate.setDate(limitDate.getDate() + 1);
    limitDate.setHours(0, 0, 0, 0);

    return raceDate < limitDate;
  };

  const getRaceId = (r) => r?.raceId ?? r?.id;

  const flagPath = (country) => {
    if (!country) return "";
    return `${process.env.PUBLIC_URL}/flags/${country
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/ /g, "_")
      .toLowerCase()}.png`;
  };

  const groupOf = (race) => {
    const d = (race?.division || "").toLowerCase().trim();
    if (d === "world tour" || d === "wt") return 0;
    if (d === "majeur" || d === "major") return 1;
    if (d.includes("continental") || d.includes("secondaire")) return 2;
    if (d.includes("pro") || d.includes("primaire")) return 3;
    
    const g = Number(race?.groupCompetitionId);
    return g; 
  };
  
  const divisionOf = (race) => {
    const g = groupOf(race);
    if (g === 0) return "World Tour";
    if (g === 1) return "Majeur";
    if (g === 2) return "Continental";
    if (g === 3) return "Pro Series";
    return race?.division || "—";
  };

  const groupLabel = (g) => {
    switch (g) {
      case 0: return "World Tour";
      case 1: return "Majeur";
      case 2: return "Continental";
      case 3: return "Pro Series";
      default: return "Autre";
    }
  };

  const isGrandTour = (race) => {
    const id = Number(getRaceId(race));
    return [87, 122].includes(id);
  };

  const fullCalendar = useMemo(() => {
    const obligatory = (races || []).filter(r => {
        const g = groupOf(r);
        return g === 0 || g === 1;
    });

    const optional = selectedRaces || [];

    const map = new Map();
    obligatory.forEach(r => map.set(getRaceId(r), r));
    optional.forEach(r => map.set(getRaceId(r), r));

    return Array.from(map.values());
  }, [races, selectedRaces]);


  const counts = useMemo(() => {
    const safeBonus = bonusRaces || [];
    let wt = 0, maj = 0, cont = 0, pro = 0, gt = 0;

    safeBonus.forEach(r => {
      const g = groupOf(r);
      if (g === 0) wt++;
      if (g === 1) maj++;
      if (g === 2) cont++;
      if (g === 3) pro++;
      if (isGrandTour(r)) gt++;
    });

    return { wt, maj, cont, pro, gt, total: safeBonus.length };
  }, [bonusRaces]);

  const { wt, maj, cont, pro, gt, total } = counts;

  const isInvalidSelection = useMemo(() => {
    if (wt > 3 || maj > 3 || cont > 3 || pro > 3) return true;
    if (gt > 2) return true;
    if (total > 12) return true;
    return total !== 12;
  }, [wt, maj, cont, pro, gt, total]);

  const viewRaces = useMemo(() => {
    let list = [...fullCalendar];

    const term = searchTerm.trim().toLowerCase();
    if (term) {
      list = list.filter((r) => {
        const name = (r?.name ?? "").toLowerCase();
        return name.includes(term);
      });
    }

    if (divisionFilter !== "all") {
      list = list.filter((r) => {
        const g = groupOf(r);
        if (divisionFilter === "wt") return g === 0;
        if (divisionFilter === "maj") return g === 1;
        if (divisionFilter === "cont") return g === 2;
        if (divisionFilter === "pro") return g === 3;
        return true;
      });
    }

    list.sort((a, b) => {
      if (sortBy === "name") return (a?.name ?? "").localeCompare(b?.name ?? "");
      if (sortBy === "division") return divisionOf(a).localeCompare(divisionOf(b));
      
      const da = parseDate(a?.date_start);
      const db = parseDate(b?.date_start);
      return da - db;
    });

    return list;
  }, [fullCalendar, searchTerm, divisionFilter, sortBy]);

  const toggleBonus = (race) => {
    if (!race) return;
    setErrorMessage("");

    if (isRacePast(race.date_start)) return;

    const id = getRaceId(race);
    const isSelected = (bonusRaces || []).some(r => getRaceId(r) === id);

    if (isSelected) {
      setBonusRaces((bonusRaces || []).filter((r) => getRaceId(r) !== id));
    } else {
      if (total >= 12) {
        setErrorMessage(t("selectBonusRace.errors.maxTotal"));
        return;
      }
      
      const g = groupOf(race);
      let currentGroupCount = 0;
      if (g === 0) currentGroupCount = wt;
      if (g === 1) currentGroupCount = maj;
      if (g === 2) currentGroupCount = cont;
      if (g === 3) currentGroupCount = pro;

      if (currentGroupCount >= 3) {
        setErrorMessage(t("selectBonusRace.errors.maxPerDivision", { division: groupLabel(g) }));
        return;
      }

      if (isGrandTour(race) && gt >= 2) {
        setErrorMessage(t("selectBonusRace.errors.maxGrandTours"));
        return;
      }

      setBonusRaces([...(bonusRaces || []), race]);
    }
  };

  const validData = () => {
    saveData()
      .then(() => setConfirmValidDataPopup(true))
      .catch((error) => {
        console.error("Erreur save:", error);
        setErrorMessage(t("selectBonusRace.errors.saveFailed"));
      });
  };

  const handleClosePopup = () => navigate("/profil");

  const sortedBonus = useMemo(() => {
    return [...(bonusRaces || [])].sort((a, b) => 
      parseDate(a?.date_start) - parseDate(b?.date_start)
    );
  }, [bonusRaces]);

  const firstLine = sortedBonus.filter((_, i) => i % 2 === 0);
  const secondLine = sortedBonus.filter((_, i) => i % 2 === 1);

  return (
    <main className="select-races-bonus">
      {confirmValidDataPopup && (
        <div className="select-races-bonus-popup">
          <div className="select-races-bonus-popup-content">
            <p>{t("selectBonusRace.popup.successLine1")}</p>
            <button onClick={handleClosePopup}>{t("common.validate")}</button>
          </div>
        </div>
      )}

      <div className="select-races-bonus-shadow-container">
        <div className="select-races-bonus-title">
          <h1>{t("selectBonusRace.title")}</h1>
        </div>
      </div>

      <div className="select-races-bonus-header">
        <div className="select-races-bonus-rules">
          <div>
            <p>{t("selectBonusRace.subtitle")}</p>
            {errorMessage && <p className="select-races-bonus-error">{errorMessage}</p>}
          </div>
        </div>
        <div className="select-races-bonus-change-step">
          <button onClick={prevStep}>{t("common.previous")}</button>
          <button onClick={validData} disabled={isInvalidSelection}>
            {t("common.validate")}
          </button>
        </div>
      </div>

      <div className="select-races-bonus-pannel-view">
        <div className="select-races-bonus-choice">
          
          <div className="select-races-bonus-controls">
            <div className="select-races-bonus-search-control">
              <label>{t("selectBonusRace.controls.search")}</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="select-races-bonus-filter-control">
              <label>Division</label>
              <select value={divisionFilter} onChange={(e) => setDivisionFilter(e.target.value)}>
                <option value="all">Toutes</option>
                <option value="wt">World Tour</option>
                <option value="maj">Majeur</option>
                <option value="pro">Pro Series</option>
                <option value="cont">Continental</option>
              </select>
            </div>
            <div className="select-races-bonus-sortby-control">
              <label>Tri</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="date">Date</option>
                <option value="name">Nom</option>
                <option value="division">Division</option>
              </select>
            </div>
          </div>

          <div className="select-races-bonus-table-container">
            <table>
              <thead>
                <tr>
                  <th>Course</th>
                  <th>Date</th>
                  <th>Cat</th>
                  <th>Div</th>
                  <th>Type</th>
                  <th>Profil</th>
                </tr>
              </thead>
              <tbody>
                {viewRaces.map((race) => {
                  const id = getRaceId(race);
                  const isSel = (bonusRaces || []).some(r => getRaceId(r) === id);
                  const isPast = isRacePast(race.date_start);

                  let rowStyle = { cursor: "pointer" };
                  
                  if (isSel) {
                    rowStyle.backgroundColor = "yellow"; 
                    rowStyle.fontWeight = "bold";
                    if (isPast) {
                        rowStyle.cursor = "not-allowed";
                        rowStyle.color = "#856404";
                        rowStyle.opacity = 0.8;
                    }
                  } else if (isPast) {
                    rowStyle.backgroundColor = "#e9ecef";
                    rowStyle.color = "#adb5bd";
                    rowStyle.cursor = "not-allowed";
                  }

                  return (
                    <tr
                      key={id}
                      onClick={() => toggleBonus(race)}
                      style={rowStyle}
                    >
                      <td>
                        <div className="select-races-bonus-table-name-container">
                          <img
                            className="select-races-table-flag-icon"
                            src={flagPath(race.country)}
                            alt={race.country}
                            width="20" height="20"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                          <span>{race.name} {isPast && "🔒"}</span>
                        </div>
                      </td>
                      <td>{formatDateForDisplay(race.date_start)}</td>
                      <td>{race.category}</td>
                      <td>{divisionOf(race)}</td>
                      <td>{race.courseType}</td>
                      <td>
                        {icons[race.profil] ? (
                          <img src={icons[race.profil]} alt={race.profil} width="25" height="25" />
                        ) : race.profil}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="select-races-bonus-choice-view">
          <div style={{marginBottom: "20px"}}>
             <p className="select-races-bonus-selected-number">WT: <span className={wt===3?"green":"red"}>{wt}</span>/3</p>
             <p className="select-races-bonus-selected-number">Maj: <span className={maj===3?"green":"red"}>{maj}</span>/3</p>
             <p className="select-races-bonus-selected-number">Cont: <span className={cont===3?"green":"red"}>{cont}</span>/3</p>
             <p className="select-races-bonus-selected-number">Pro: <span className={pro===3?"green":"red"}>{pro}</span>/3</p>
             <p className="select-races-bonus-selected-number">Total: <span className={total===12?"green":"red"}>{total}</span>/12</p>
          </div>

          <div className="select-races-bonus-scroll-container">
            <div className="select-races-bonus-line-container">
              {sortedBonus.map((race) => {
                 const id = getRaceId(race);
                 const isPast = isRacePast(race.date_start);
                 return (
                  <div 
                    className="select-races-bonus-selected-cell" 
                    key={id}
                    style={{
                        backgroundColor: isPast ? "yellow" : "white",
                        opacity: isPast ? 0.7 : 1,
                        borderColor: isPast ? "#ffeeba" : "var(--primary-color)"
                    }}
                  >
                    <div className="select-races-bonus-selected-info">
                      <div className="select-races-bonus-selected-name-container">
                         <img src={flagPath(race.country)} width="20" alt=""/>
                         <h4 style={{color: isPast ? "#856404" : "black"}}>
                            {race.name} {isPast && "🔒"}
                         </h4>
                      </div>
                      <p>{formatDateForDisplay(race.date_start)}</p>
                    </div>

                    {!isPast && (
                        <img
                            src={`${process.env.PUBLIC_URL}/icons/cancel.png`}
                            width="17" height="17"
                            style={{cursor: "pointer"}}
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleBonus(race);
                            }}
                            alt="Remove"
                        />
                    )}
                  </div>
                 );
              })}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default SelectBonusRace;
